import React, { useEffect, useRef } from "react";
import { Sparkles, Compass, ShieldCheck, ArrowDown, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { useBridgeStore } from "@/lib/dreamBridge";
import { scrollToElementAfterRender, focusWithoutScroll } from "@/lib/scroll";

// Sibling subcomponents in this folder
import SavedProgressControls from "./SavedProgressControls";
import WorldInsideMind from "./WorldInsideMind";
import DiscoverWhatMatters from "./DiscoverWhatMatters";
import ActionPlan from "./ActionPlan";
import WhenDistressing from "./WhenDistressing";
import "./bridge.css";
export default function DreamBridge() {
  const requestToken = useBridgeStore(s => s.requestToken);
  const previousTokenRef = useRef<number>(requestToken);

  // Watch requestToken from the store: smooth-scroll to #bridge-matters & move focus to heading
  // Guarded: only runs when token actually increments (greater than 0 and changed),
  // accounting for React StrictMode and initial mount without double-scrolling or firing on first render.
  useEffect(() => {
    if (requestToken > 0 && requestToken !== previousTokenRef.current) {
      previousTokenRef.current = requestToken;
      scrollToElementAfterRender(() => document.getElementById("bridge-matters"), {
        block: "start",
        onlyIfNeeded: false
      });
      // Wait for the render tick to focus the heading cleanly without default browser jump-scroll
      window.requestAnimationFrame(() => {
        const target = document.getElementById("bridge-matters");
        if (target) {
          const heading = target.querySelector<HTMLElement>("h3, h2, [tabindex='-1']");
          if (heading) {
            focusWithoutScroll(heading);
          }
        }
      });
    }
  }, [requestToken]);
  return <section id="dream-bridge" data-nav="dark" className="relative w-full max-w-full overflow-hidden bg-transparent text-foreground py-14 sm:py-20 lg:py-28">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full space-y-12 sm:space-y-16">
        {/* Main Section Heading */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <SectionHeading eyebrow="From Fiction to Agency" title="The Dream-to-Reality Bridge" description="Turn what your imagination longs for into achievable, grounded changes in your real daily life. Explore the core values behind alternate lives and build a personalized starter plan." align="center" as="h2" />

          {/* Honesty & Architecture Banner */}
          <div className="p-4 sm:p-5 rounded-xl border border-border/80 bg-card/70 shadow-lg text-xs text-muted-foreground backdrop-blur-md max-w-3xl mx-auto text-left space-y-2">
            <div className="flex items-start gap-2.5">
              <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1 leading-relaxed">
                <span className="font-semibold text-foreground">Honesty Notice: </span>
                This bridge is built from carefully written templates and branching rules, not AI. The resulting plan is an educational suggestion, not a prediction and not therapy. Everything is optional, completely private, and processed right inside your browser—nothing is sent anywhere.
              </div>
            </div>
          </div>

          {/* Step Navigation Anchor Links (Min 44px touch targets) */}
          <nav aria-label="Dream to Reality Bridge step navigation" className="pt-2 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            <Link to="#bridge-mind" className="dtr-step-link">
              <span className="font-semibold text-primary">1</span>
              <span>World in Mind</span>
            </Link>
            <Link to="#bridge-matters" className="dtr-step-link">
              <span className="font-semibold text-primary">2</span>
              <span>What Matters</span>
            </Link>
            <Link to="#bridge-plan" className="dtr-step-link">
              <span className="font-semibold text-primary">3</span>
              <span>Action Plan</span>
            </Link>
            <Link to="#bridge-support" className="dtr-step-link">
              <span className="font-semibold text-primary">4</span>
              <span>Support & Care</span>
            </Link>
          </nav>
        </div>

        {/* Opt-in Local Storage & Reset Controls */}
        <SavedProgressControls />

        {/* Section 1: The World Inside Your Mind */}
        <WorldInsideMind />

        {/* Section 2: Discover What Matters */}
        <DiscoverWhatMatters />

        {/* Section 3: Your Grounded Action Plan */}
        <ActionPlan />

        {/* Section 4: When Daydreaming Feels Distressing */}
        <WhenDistressing />
      </div>
    </section>;
}